import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import ProjectAddTo from '~/components/legacy/ProjectAddTo.jsx';

let nextDrawerInstanceId = 0;

function formatLength(length) {
  let minutes = Math.floor(length / 60);
  let seconds = length % 60;

  if (minutes < 10) {
    minutes = '0' + minutes;
  }

  if (seconds < 10) {
    seconds = '0' + seconds;
  }

  return minutes + ':' + seconds;
}

function formatEnergyPercent(energyPercent) {
  if (energyPercent === null || typeof energyPercent === 'undefined') {
    return null;
  }

  return `${energyPercent}%`;
}

class TrackDetailsDrawer extends Component {
  constructor(props) {
    super(props);

    this.createPortalRoot = this.createPortalRoot.bind(this);
    this.destroyPortalRoot = this.destroyPortalRoot.bind(this);
    this.renderDrawerMarkup = this.renderDrawerMarkup.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleProjectActionOpenChange = this.handleProjectActionOpenChange.bind(this);
    this.state = { projectActionOpen: false };
    this.drawerTitleId = `track-details-drawer-title-${nextDrawerInstanceId += 1}`;
    this.panelRef = React.createRef();
    this.closeButtonRef = React.createRef();
    this.previousActiveElement = null;
  }

  componentDidMount() {
    this.createPortalRoot();
    if (this.props.visible) {
      this.focusDrawer();
    }
  }

  componentWillUnmount() {
    if (this.props.visible) {
      this.restoreFocus();
    }
    this.destroyPortalRoot();
  }

  componentDidUpdate(previousProps) {
    if (!previousProps.visible && this.props.visible) {
      this.focusDrawer();
    }

    if (previousProps.visible && !this.props.visible) {
      this.restoreFocus();
    }
  }

  focusDrawer() {
    this.previousActiveElement = typeof document === 'undefined' ? null : document.activeElement;
    this.closeButtonRef.current?.focus();
  }

  restoreFocus() {
    if (this.previousActiveElement && this.previousActiveElement.isConnected !== false && typeof this.previousActiveElement.focus === 'function') {
      this.previousActiveElement.focus();
    }
    this.previousActiveElement = null;
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.props.onRequestClose();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = Array.from(event.currentTarget.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((element) => !element.closest('[hidden]'));
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  handleProjectActionOpenChange(isOpen) {
    this.setState({ projectActionOpen: isOpen });
  }

  createPortalRoot() {
    if (typeof document === 'undefined' || this.portalRoot) {
      return;
    }

    this.portalRoot = document.createElement('div');
    this.portalRoot.className = 'track-details-drawer__portal';
    document.body.appendChild(this.portalRoot);
  }

  destroyPortalRoot() {
    if (!this.portalRoot) {
      return;
    }

    if (this.portalRoot.parentNode) {
      this.portalRoot.parentNode.removeChild(this.portalRoot);
    }

    this.portalRoot = null;
  }

  renderDrawerMarkup() {
    if (!this.props.visible) {
      return null;
    }

    const closeLabel = this.props.locale === 'de' ? 'Track-Details schließen' : 'Close track details';
    const detailsLinkLabel = this.props.locale === 'de' ? 'Zur Detailseite' : 'View track details';
    const playLabel = this.props.locale === 'de' ? 'Track abspielen' : 'Play track';
    const favoriteLabel = this.props.favorited ?
      (this.props.locale === 'de' ? 'Gemerkt' : 'Saved') :
      (this.props.locale === 'de' ? 'Merken' : 'Save');
    const shareLabel = this.props.locale === 'de' ? 'Teilen' : 'Share';
    const demoLabel = this.props.showDownloadButton ?
      (this.props.locale === 'de' ? 'Download' : 'Download') :
      (this.props.locale === 'de' ? 'Demo' : 'Demo');
    const licenseLabel = this.props.locale === 'de' ? 'Lizenz' : 'License';
    const closeLinkLabel = this.props.locale === 'de' ? 'Schließen' : 'Close';
    const durationLabel = this.props.locale === 'de' ? 'Dauer' : 'Duration';
    const collectingLabel = this.props.locale === 'de' ? 'Verwertungsgesellschaft' : 'Collecting society';
    const contentIdLabel = this.props.locale === 'de' ? 'YouTube ContentID' : 'YouTube ContentID';
    const energyLabel = this.props.locale === 'de' ? 'Energie' : 'Energy';
    const variantsHeading = this.props.locale === 'de' ? 'Track-Varianten' : 'Track variants';
    const bundlesHeading = this.props.locale === 'de' ? 'Bundles' : 'Bundles';
    const activeVariant = (this.props.trackVariants || []).find((variant) => String(variant.id) === String(this.props.activeTrackVariantId));
    const collectingValue = this.props.royaltyFree === null || this.props.royaltyFree === true ?
      (this.props.locale === 'de' ? 'GEMAfrei' : 'Royalty free') :
      this.props.proShortName;
    const contentIdValue = this.props.youtubeContentIdEnabled ?
      (this.props.locale === 'de' ? 'Aktiv' : 'Enabled') :
      (this.props.locale === 'de' ? 'Nein' : 'No');
    const energyValue = formatEnergyPercent(this.props.energyPercent);
    const playIconClass = this.props.playing && !(parseInt(this.props.activeTrackVariantId, 10) > 0) ? 'fa fa-pause' : 'fa fa-play';
    const variants = (this.props.trackVariants || []).map((variant) => {
      const active = parseInt(this.props.activeTrackVariantId, 10) === variant.id;
      const itemClassName = active ?
        'track-details-drawer__variant-button track-details-drawer__variant-button--active' :
        'track-details-drawer__variant-button';
      const iconClassName = this.props.playing && active ? 'fa fa-pause' : 'fa fa-play';

      return (
        <button
          type='button'
          key={'track-details-variant-' + variant.id}
          className={itemClassName}
          onClick={(event) => this.props.onVariantSelect(variant, event)}>
          <span className='track-details-drawer__variant-icon'><i className={iconClassName}></i></span>
          <span className='track-details-drawer__variant-title'>{variant.title}</span>
        </button>
      );
    });
    const bundles = (this.props.includedMediaBundles || []).map((mediaBundle) => (
      <a
        key={'track-details-bundle-' + mediaBundle.media_bundle_id}
        href={'/bundles/' + mediaBundle.permalink}
        className='track-details-drawer__bundle-link'
        target='_blank'
        rel='noreferrer'>
        <span className='track-details-drawer__bundle-link-icon'><i className='fa fa-arrow-right'></i></span>
        <span className='track-details-drawer__bundle-link-text'>Bundle: {mediaBundle.title}</span>
      </a>
    ));

    return (
      <div className='track-details-drawer'>
        <div className='track-details-drawer__backdrop' aria-hidden='true' onClick={this.props.onRequestClose}></div>
        <div ref={this.panelRef} className='track-details-drawer__panel' role='dialog' aria-modal='true' aria-hidden={this.state.projectActionOpen} aria-labelledby={this.drawerTitleId} tabIndex='-1' onKeyDown={this.handleKeyDown} onClick={(event) => event.stopPropagation()}>
          <div className='track-details-drawer__header'>
            <div id={this.drawerTitleId} className='track-details-drawer__heading'>
              {this.props.locale === 'de' ? 'Track-Details' : 'Track details'}
            </div>
            <button ref={this.closeButtonRef} type='button' className='track-details-drawer__close' onClick={this.props.onRequestClose} aria-label={closeLabel}>
              <i className='fa fa-times'></i>
            </button>
          </div>

          <div className='track-details-drawer__body'>
            <div className='track-details-drawer__preview'>
              <div className='track-details-drawer__image-wrapper'>
                {this.props.trackImageMarkup ? (
                  <div className='track-details-drawer__image' dangerouslySetInnerHTML={{ __html: this.props.trackImageMarkup }}></div>
                ) : null}
                <button type='button' className='track-details-drawer__play-button' onClick={this.props.onPlayToggle} aria-label={playLabel}>
                  <i className={playIconClass}></i>
                </button>
              </div>
              <div className='track-details-drawer__preview-meta'>
                <div className='track-details-drawer__title-row'>
                  <span className='track-details-drawer__title'>{this.props.trackTitle}</span>
                  <span className='track-details-drawer__length'>{formatLength(this.props.length)}</span>
                </div>
                {this.props.trackArtist ? <div className='track-details-drawer__artist'>{this.props.trackArtist}</div> : null}
              </div>
            </div>

            <div className='track-details-drawer__actions'>
              <button type='button' className='track-details-drawer__action' onClick={this.props.onFavoriteToggle} aria-label={favoriteLabel}>
                <i className={this.props.favorited ? 'fa fa-heart' : 'fa fa-heart-o'}></i>
                <span className='track-details-drawer__action-label'>{favoriteLabel}</span>
              </button>
              {this.props.projectActionEnabled ? (
                <ProjectAddTo
                  trackId={this.props.trackId}
                  trackTitle={this.props.trackTitle}
                  trackVariantId={activeVariant?.id || null}
                  trackVariantTitle={activeVariant?.title}
                  trackVariants={this.props.trackVariants}
                  locale={this.props.locale}
                  enabled={true}
                  onOpenChange={this.handleProjectActionOpenChange}
                  portalClassName='catalog-project-action__portal--track-details-drawer' />
              ) : null}
              <button type='button' className='track-details-drawer__action' onClick={this.props.onShare} aria-label={shareLabel}>
                <i className='fa fa-share-alt'></i>
                <span className='track-details-drawer__action-label'>{shareLabel}</span>
              </button>
              <button type='button' className='track-details-drawer__action' onClick={this.props.onDemo} aria-label={demoLabel}>
                <i className={this.props.showDownloadButton ? 'fa fa-download' : 'fa fa-download'}></i>
                <span className='track-details-drawer__action-label'>{demoLabel}</span>
              </button>
              {!this.props.freeMode ? (
                <button type='button' className='track-details-drawer__action track-details-drawer__action--primary' onClick={this.props.onCart} aria-label={licenseLabel}>
                  <i className={this.props.inCart ? 'fa fa-shopping-cart' : 'fa fa-shopping-cart'}></i>
                  <span className='track-details-drawer__action-label'>{licenseLabel}</span>
                </button>
              ) : null}
            </div>
            <div className='track-details-drawer__meta-list'>
              <div className='track-details-drawer__meta-row'>
                <span className='track-details-drawer__meta-label'>{durationLabel}</span>
                <span className='track-details-drawer__meta-value'>{formatLength(this.props.length)}</span>
              </div>
              <div className='track-details-drawer__meta-row'>
                <span className='track-details-drawer__meta-label'>{collectingLabel}</span>
                <span className='track-details-drawer__meta-value'>{collectingValue}</span>
              </div>
              <div className='track-details-drawer__meta-row'>
                <span className='track-details-drawer__meta-label'>{contentIdLabel}</span>
                <span className='track-details-drawer__meta-value'>{contentIdValue}</span>
              </div>
              {energyValue ? (
                <div className='track-details-drawer__meta-row'>
                  <span className='track-details-drawer__meta-label'>{energyLabel}</span>
                  <span className='track-details-drawer__meta-value'>{energyValue}</span>
                </div>
              ) : null}
            </div>

            <a
              href={this.props.showcaseUrl}
              target='_blank'
              rel='noreferrer'
              className='button button--ghost button--full track-details-drawer__details-link'>
              {detailsLinkLabel}
            </a>
            <button type='button' className='button button--text track-details-drawer__details-close' onClick={this.props.onRequestClose}>
              {closeLinkLabel}
            </button>

            {variants.length > 0 ? (
              <div className='track-details-drawer__variants'>
                <div className='track-details-drawer__section-title'>{variantsHeading}</div>
                <div className='track-details-drawer__variant-list'>
                  {variants}
                </div>
              </div>
            ) : null}

            {bundles.length > 0 ? (
              <div className='track-details-drawer__bundles'>
                <div className='track-details-drawer__section-title'>{bundlesHeading}</div>
                <div className='track-details-drawer__bundle-list'>
                  {bundles}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const drawerMarkup = this.renderDrawerMarkup();

    if (!drawerMarkup) {
      return null;
    }

    if (this.portalRoot) {
      return ReactDOM.createPortal(drawerMarkup, this.portalRoot);
    }

    return drawerMarkup;
  }
}

TrackDetailsDrawer.defaultProps = {
  locale: 'de',
  freeMode: false,
  projectActionEnabled: false,
  energyPercent: null,
  trackVariants: [],
  includedMediaBundles: []
};

export default TrackDetailsDrawer;
